import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { infoProject as fetchInfoProject } from '../../../../api/common';

import {
  countries as fetchCountries,
  Country,
  Currency,
  CurrentIp,
  currentIp as fetchCurrentIp,
  playerFields as fetchPlayerFields,
  PlayerFields,
} from '../../api/common';
import { camelize } from '../../helpers/common';
import { BeforeInstallPromptEvent } from 'types/common';
import { DEFAULT_CURRENCIES } from 'helpers/conf';

export interface CommonContextData {
  playerFields: PlayerFields | undefined;
  project: { availableReferralSystem: boolean } | null;
  withCaptcha: boolean;
  currencies: Currency[];
  countries: Country[];
  currentIp: CurrentIp | undefined;
}

export interface CommonContextActions {
  fetchProject: () => void;
}

export const CommonContext = createContext<
  CommonContextData & CommonContextActions
>(null as any);

const initialState = {
  playerFields: undefined,
  project: null,
  withCaptcha: false,
  currencies: DEFAULT_CURRENCIES,
  countries: [],
  userGroups: [],
  currentIp: undefined,
};

interface CommonContextProviderProps {
  notAllowedCountries?: { list: string[]; component: JSX.Element };
}

export const CommonContextProvider: FC<CommonContextProviderProps> = ({
  notAllowedCountries,
  children,
}) => {
  const [blockService, setBlockService] = useState<boolean>();

  const [state, setState] = useState<CommonContextData>(initialState);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: currentIp } = await fetchCurrentIp()
          .then(({ data }) => ({ data: JSON.parse(data) }))
          .then(({ data }) => ({ data: camelize(data) as CurrentIp }));

        if (
          notAllowedCountries != null &&
          notAllowedCountries.list.includes(currentIp.countryCode)
        ) {
          setBlockService(true);

          return;
        }

        setBlockService(false);

        const [
          { data: playerFields },
          { data: countries },
        ] = await Promise.all([fetchPlayerFields(), fetchCountries()]);

        setState((s) => ({
          ...s,
          playerFields,
          countries,
          currentIp,
        }));
      } catch {
        setState(initialState);
      }
    };

    fetchData();
  }, [notAllowedCountries]);

  const ref = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      ref.current = event as BeforeInstallPromptEvent;
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const fetchProject = useCallback(async () => {
    try {
      const { data: project } = await fetchInfoProject();
      setState((prevState: CommonContextData) => ({
        ...prevState,
        project,
      }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  if (blockService == null) return null;

  if (blockService)
    return <>{notAllowedCountries != null && notAllowedCountries.component}</>;

  return (
    <CommonContext.Provider value={{ ...state, fetchProject }}>
      <button
        id="installPWAButton"
        style={{ visibility: 'hidden', position: 'absolute' }}
        onClick={() => {
          if (ref.current) ref.current.prompt();
        }}
      />
      {children}
    </CommonContext.Provider>
  );
};
